/**
 * 어드민 맥스매치 운영 API.
 * Base: /admin/max-match/ops — Swagger Admin MaxMatch Ops Query/Command
 */
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

/** 운영 건 상태 */
export type MaxMatchOpsStatus = 'BEFORE_MATCH' | 'MATCHED';

/** 이미지 OCR 상태 */
export type MaxMatchOpsOcrStatus = 'PENDING' | 'DONE' | 'FAILED';

/** 결과 행 상태 */
export type MaxMatchOpsResultStatus = 'CHANGED' | 'UNCHANGED';

export interface MaxMatchOpsListItem {
  summaryId: number;
  createdAdminEmail: string;
  pharmaCompanyName: string;
  clinicName: string;
  createdAt: string;
  imageCount: number;
  /** 매칭 전 null */
  unchangedCount: number | null;
  changedCount: number | null;
  originEstimatedTotalAmount: number | null;
  newEstimatedTotalAmount: number | null;
  status: MaxMatchOpsStatus | string;
}

export interface MaxMatchOpsListParams {
  searchKeyword?: string;
  /** 전체 조회 시 생략 — ALL 보내지 않음 */
  status?: MaxMatchOpsStatus;
}

export interface MaxMatchOpsDetail {
  summaryId: number;
  pharmaCompanyName: string;
  clinicName: string;
  /** 처방 기준 년월 YYYYMM */
  refYm: string;
  status: MaxMatchOpsStatus | string;
  createdAt: string;
  matchedAt: string | null;
}

export interface MaxMatchOpsImage {
  imageId: number;
  imageCode: string;
  imageUrl: string;
  originalFilename?: string | null;
  isConfirmed: boolean;
  ocrStatus?: MaxMatchOpsOcrStatus | string;
}

export interface MaxMatchOpsMr {
  mrId?: number;
  drugCode: string;
  hasDrugInfo?: boolean | null;
  pharmaCompanyName?: string;
  drugName?: string;
  price?: number;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
  totalAmount?: number;
  __tempRowKey?: string;
}

export interface MaxMatchOpsMrPayload {
  drugCode: string;
  drugName?: string;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

export interface MaxMatchOpsPresignedUrl {
  filename: string;
  presignedUrl: string;
  uploadKey: string;
  shortKey: string;
}

export interface MaxMatchOpsCreateImage {
  shortKey: string;
  uploadKey: string;
  originalFilename?: string;
}

export interface MaxMatchOpsCreateRequest {
  pharmaCompanyName: string;
  clinicName: string;
  images: MaxMatchOpsCreateImage[];
}

export interface MaxMatchOpsCreateResponse {
  summaryId: number;
  refYm: string;
  imageCount: number;
}

export interface MaxMatchOpsRunResult {
  summaryId: number;
  originEstimatedTotalAmount: number;
  newEstimatedTotalAmount: number;
  amountDifference: number;
  unchangedCount: number;
  changedCount: number;
  matchedAt: string;
}

export interface MaxMatchOpsPostProcessRow {
  rowId: string;
  rawDrugCode?: string | null;
  rawDrugName?: string | null;
  rawPharmaCompanyName?: string | null;
  rawUnitPrice?: number | null;
  rawUsageAmount?: number | null;
  rawClaimAmount?: number | null;
  rawInHouseUsageAmount?: number | null;
}

export interface MaxMatchOpsPostProcessSuggested {
  drugCode?: string | null;
  drugName?: string | null;
  pharmaCompanyName?: string | null;
  unitPrice?: number | null;
}

export interface MaxMatchOpsPostProcessItem {
  rowId?: string;
  status?: 'MATCHED' | 'LIKELY_MATCHED' | 'REVIEW_REQUIRED' | string;
  applied?: boolean;
  raw?: MaxMatchOpsPostProcessRow;
  suggested?: MaxMatchOpsPostProcessSuggested | null;
}

export interface MaxMatchOpsPostProcessResponse {
  prescriptionYearMonth?: string;
  items?: MaxMatchOpsPostProcessItem[];
}

const OCR_TIMEOUT_MS = 600_000;

export interface MaxMatchOpsExcelRow {
  createdAdminEmail?: string;
  pharmaCompanyName?: string;
  clinicName?: string;
  refYm?: string;
  pharmaCompanyNameOrig?: string;
  drugCodeOrig?: string;
  drugNameOrig?: string;
  outHospitalQuantity?: number;
  priceOrig?: number;
  totalAmount?: number;
  standardCommissionRateOrig?: number;
  commissionAmountOrig?: number;
  pharmaCompanyNameNew?: string;
  drugCodeNew?: string;
  drugNameNew?: string;
  priceNew?: number;
  standardCommissionRateNew?: number;
  commissionAmountNew?: number;
  status?: MaxMatchOpsResultStatus | string;
  createdDate?: string;
  [key: string]: unknown;
}

export const maxMatchOpsService = {
  list: async (
    search: MaxMatchOpsListParams,
    pageable: Pageable,
  ): Promise<PageResponse<MaxMatchOpsListItem>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
    };
    if (search.searchKeyword) params.searchKeyword = search.searchKeyword;
    // 전체는 status 파라미터 생략 (ALL 금지)
    if (search.status) params.status = search.status;

    const response = await apiClient.get(createApiPath.maxMatch.ops(), {
      params,
    });
    return response.data;
  },

  getDetail: async (summaryId: number): Promise<MaxMatchOpsDetail> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.opsDetail(summaryId),
    );
    return response.data;
  },

  getImages: async (summaryId: number): Promise<MaxMatchOpsImage[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.opsImages(summaryId),
    );
    // 업로드 순서 유지 — 서버 정렬 그대로 사용
    return Array.isArray(response.data) ? response.data : [];
  },

  getMrs: async (imageId: number): Promise<MaxMatchOpsMr[]> => {
    const response = await apiClient.get(createApiPath.maxMatch.opsMrs(imageId));
    return Array.isArray(response.data) ? response.data : [];
  },

  createPresignedUrls: async (
    filenames: string[],
  ): Promise<MaxMatchOpsPresignedUrl[]> => {
    const response = await apiClient.post<MaxMatchOpsPresignedUrl[]>(
      createApiPath.maxMatch.opsPresignedUrls(),
      { filenames },
    );
    return response.data;
  },

  create: async (
    request: MaxMatchOpsCreateRequest,
  ): Promise<MaxMatchOpsCreateResponse> => {
    const response = await apiClient.post(
      createApiPath.maxMatch.ops(),
      request,
    );
    return response.data;
  },

  addMr: async (imageId: number, payload: MaxMatchOpsMrPayload): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.opsMrs(imageId), payload);
  },

  addMrsBatch: async (
    imageId: number,
    payload: MaxMatchOpsMrPayload[],
  ): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.opsMrsBatch(imageId), payload);
  },

  updateMr: async (mrId: number, payload: MaxMatchOpsMrPayload): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.opsMr(mrId), payload);
  },

  deleteMr: async (mrId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.opsMr(mrId));
  },

  deleteAllMrs: async (imageId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.opsMrs(imageId));
  },

  confirmImage: async (imageId: number): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.opsConfirmImage(imageId));
  },

  confirmBatch: async (imageIds: number[]): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.opsConfirmBatch(), { imageIds });
  },

  deleteImage: async (imageId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.opsImage(imageId));
  },

  deleteSummary: async (summaryId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.opsDetail(summaryId));
  },

  run: async (summaryId: number): Promise<MaxMatchOpsRunResult> => {
    const response = await apiClient.post(
      createApiPath.maxMatch.opsRun(summaryId),
      undefined,
      { timeout: OCR_TIMEOUT_MS },
    );
    return response.data;
  },

  getExcelDetails: async (
    summaryId: number,
  ): Promise<MaxMatchOpsExcelRow[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.opsExcel(summaryId),
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  /** OCR row를 기준월 의약품 마스터와 대조 */
  postProcessOcr: async (payload: {
    prescriptionYearMonth?: string;
    source?: string;
    mode?: 'OBSERVE' | 'SUGGEST' | 'APPLY';
    rows: MaxMatchOpsPostProcessRow[];
  }): Promise<MaxMatchOpsPostProcessResponse> => {
    const response = await apiClient.post(
      createApiPath.maxMatch.miniWebOcrPostProcess(),
      payload,
      { timeout: OCR_TIMEOUT_MS },
    );
    return response.data;
  },
};
